import moment from 'moment';
import dayjs from 'dayjs';

import { listAllDeliveries } from 'services/shipments';
import {
  NEW_ORDERS_STATE_CODES,
  PICKUP_CANCELLATION_REASON,
  PICKUP_CANCELLATION_REASON_DESCRIPTION,
  PICKUP_STATUS
} from 'constants/pickups';
import { LOCALE } from 'constants/intl-wrapper';
import { formatAreaZone } from 'utils/shipments';
import { dates } from 'utils/helpers';

import {
  getLocaleFromLocalStorage,
  isDashboardArabic
} from 'utils/intl-wrapper';
import { notify } from 'components/Notify/Notify';
import { customizedFmt, fmt } from 'components/IntlWrapper/IntlWrapper';
import { DAYS_OF_WEEK } from 'constants/helpers';

export const pickupsStatus = {
  created: ['Requested'],
  out_for_pickup: ['Route Assigned', 'Picking up'],
  completed: ['Picked up'],
  canceled: ['Canceled']
};

export const mapCancelReason = {
  'لم استطيع الوصول للبيزنس': 'business_unreachable',
  "Can't reach business location": 'business_unreachable',
  'Rescheduled as per business request': 'business_reschedule',
  'تم التاجيل بناء على طلب البيزنس': 'business_reschedule',
  'Orders have no tracking numbers': 'orders_no_tn',
  'الاوردرات بدون رقم تتبع': 'orders_no_tn',
  'البيزنس يحتاج فلاير': 'business_flyer',
  'Business has no packing': 'business_flyer',
  'Orders are not ready for pickup': 'orders_not_ready',
  'الاوردرات غير جاهزة': 'orders_not_ready',
  'Failed to pickup orders': 'orders_failed',
  'تم الفشل فى استلام الاوردرات': 'orders_failed',
  'The business canceled the pickup': 'business_cancelled',
  'البيزنس قام بالالغاء': 'business_cancelled'
};

export const mapStateToStatus = (state) => {
  return Object.keys(pickupsStatus).find((key) =>
    pickupsStatus[key].includes(state)
  );
};

export const disabledDate = (
  current,
  date,
  pickupCutoffTime,
  holidaysDates
) => {
  const hours = date?.hour();
  const isFriday = dayjs(current).day() === DAYS_OF_WEEK.FRIDAY;

  const isHoliday = holidaysDates?.some((holiday) =>
    current.isSame(holiday, 'day')
  );

  if (isHoliday || isFriday) {
    return true;
  }

  if (hours >= pickupCutoffTime) {
    return current && current < date.endOf('day');
  } else return current && current < date.startOf('day');
};

export const UTCWithZeroTime = (date) => {
  if (moment.isMoment(date)) {
    return date.format('YYYY-MM-DD');
  } else {
    return moment.utc(date).format('YYYY-MM-DD');
  }
};

export const setPickupFlag = async ({ skipRequest } = {}) => {
  try {
    let canCreatePickup = skipRequest;

    if (!skipRequest) {
      const { deliveries } = await listAllDeliveries({
        stateCodes: NEW_ORDERS_STATE_CODES,
        limit: 1,
        createdAtStart: dayjs().subtract(30, 'day').startOf('day').format(),
        createdAtEnd: dayjs().endOf('day').format()
      });

      canCreatePickup = deliveries?.length > 0;
    }

    localStorage.setItem('canCreatePickup', canCreatePickup);
  } catch (error) {
    notify({ msg: error.message, error });
  }
};

export const getPickupAddress = (address) => {
  const city =
    getLocaleFromLocalStorage() === LOCALE.EN
      ? address?.city?.name
      : address?.city?.nameAr;
  const area = formatAreaZone(address);
  const zone =
    getLocaleFromLocalStorage() === LOCALE.AR && address.zone?.nameAr
      ? address?.zone?.nameAr
      : address?.zone?.name;
  const district =
    getLocaleFromLocalStorage() === LOCALE.AR && address.district?.nameAr
      ? address?.district?.nameAr
      : address?.district?.name;

  return {
    fullAddress: address,
    city,
    area,
    zone,
    district
  };
};

export const mapState = (state, scheduledDate, cancelReason, exceptionCode) => {
  switch (state) {
    case PICKUP_STATUS.REQUESTED:
      return {
        label: 'created',
        tooltipContent: fmt(
          {
            id: `pickups.pickup_info.table_cells.pickup_tooltips.created`
          },
          {
            pickupDate: dates(scheduledDate, 'ddd DD MMM')
          }
        )
      };
    case PICKUP_STATUS.CANCELED:
      return {
        label: 'canceled',
        tooltipTitle:
          (cancelReason || exceptionCode) &&
          (exceptionCode && PICKUP_CANCELLATION_REASON[exceptionCode]
            ? PICKUP_CANCELLATION_REASON[exceptionCode]
            : cancelReason && cancelReason.includes('_')
            ? fmt({
                id: `pickups.cancel_reasons.${cancelReason}`
              })
            : cancelReason && mapCancelReason[cancelReason]
            ? fmt({
                id: `pickups.cancel_reasons.${mapCancelReason[cancelReason]}`
              })
            : cancelReason && cancelReason),
        tooltipContent:
          exceptionCode && PICKUP_CANCELLATION_REASON[exceptionCode]
            ? PICKUP_CANCELLATION_REASON_DESCRIPTION[exceptionCode]
            : fmt({
                id: `pickups.pickup_info.table_cells.pickup_tooltips.canceled`
              })
      };
    case PICKUP_STATUS.EXCEPTION:
      return {
        label: 'exception',
        tooltipContent: fmt({
          id: `pickups.pickup_info.table_cells.pickup_tooltips.exception`
        })
      };
    case PICKUP_STATUS.PICKEDUP:
      return {
        label: 'picked_up',
        tooltipContent: fmt({
          id: `pickups.pickup_info.table_cells.pickup_tooltips.completed`
        })
      };
    case PICKUP_STATUS.ROUTE_ASSIGNED:
    case PICKUP_STATUS.PICKINGUP:
      return {
        label: 'out_for_pickup',
        tooltipContent: fmt({
          id: `pickups.pickup_info.table_cells.pickup_tooltips.out_for_pickup`
        })
      };
    default:
      return {
        label: 'created',
        tooltipContent: fmt(
          {
            id: `pickups.pickup_info.table_cells.pickup_tooltips.created`
          },
          {
            pickupDate: dates(scheduledDate, 'ddd DD MMM')
          }
        )
      };
  }
};

export const renderFullPickUpAddress = ({ address, lang }) => {
  const { AR } = LOCALE;

  const { buildingNumber, floor, apartment, firstLine, district, city } =
    address || {};

  const localize = lang === AR ? customizedFmt(AR) : fmt;

  return `
      ${
        buildingNumber
          ? `${localize({
              id: 'settings.pickup_locations.table_cells.bld'
            })}: ${buildingNumber},`
          : ''
      }
      ${
        floor
          ? `${localize({
              id: 'settings.pickup_locations.table_cells.flr'
            })}: ${floor}`
          : ''
      }
      ${
        apartment
          ? `${localize({
              id: 'settings.pickup_locations.table_cells.apt'
            })}: ${apartment},`
          : ''
      }
      ${
        firstLine
          ? `${localize({
              id: 'settings.pickup_locations.table_cells.street'
            })}: ${firstLine},`
          : ''
      }
      ${
        district
          ? `${localize({ id: 'location_form_labels.area' })}: 
          ${
            (isDashboardArabic() || lang === AR) && district.nameAr
              ? district.nameAr
              : district.name
          },`
          : ''
      }
      ${
        city
          ? `${localize({ id: 'location_form_labels.city' })}: 
          ${
            (isDashboardArabic() || lang === AR) && city.nameAr
              ? city.nameAr
              : city.name
          }
        `
          : ''
      }
      `;
};
